import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { faCircleArrowRight } from '@fortawesome/pro-light-svg-icons'
import { Button, Card, Text, CardColors } from '@/ui'
import { selectPaySchedule, selectUpcomingPayroll } from '../payroll.selectors'
import { PayrollCalendar } from './PayrollCalendar'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { TypographyStyles } from '@/styles'

const formatDate = (date: string | undefined) => {
  return date ? moment(date).format(DATE_FORMATS.DISPLAY_SHORT) : ''
}

export const UpcomingPayroll = () => {
  const navigate = useNavigate()

  const upcomingPayroll = useReselector(selectUpcomingPayroll)
  const paySchedule = useReselector(selectPaySchedule)

  if (!upcomingPayroll) {
    return null
  }

  const {
    payroll_uuid,
    processed,
    pay_period: { start_date, end_date },
    payroll_deadline,
  } = upcomingPayroll

  const isAutoPayroll = Boolean(paySchedule?.auto_payroll)

  return (
    <div className="mt-3">
      <Card color={CardColors.LEVEL_1} className="p-4">
        <div className="flex flex-col md:flex-row">
          {/* Left Column */}
          <div className="w-full md:w-1/2 flex flex-col gap-4">
            {processed ? (
              <>
                {start_date && end_date && (
                  <Text variant={TypographyStyles.H1}>
                    You&#39;ve run payroll for Pay Period:
                    <br />
                    {formatDate(start_date)} - {formatDate(end_date)}
                  </Text>
                )}
                <Text variant={TypographyStyles.BODY_SM}>
                  Great work, you are all set! An automatic email will notify
                  you when it is time to run another regular payroll.
                </Text>
              </>
            ) : (
              <>
                <Text variant={TypographyStyles.H1}>Regular Payroll</Text>

                {start_date && end_date && (
                  <Text variant={TypographyStyles.BODY_MD}>
                    {formatDate(start_date)} - {formatDate(end_date)}
                  </Text>
                )}

                {isAutoPayroll ? (
                  <Text variant={TypographyStyles.BODY_SM}>
                    Because you have set up Automatic Payroll, no further action
                    is needed.
                    <br />
                    Your next payroll will automatically run on{' '}
                    <b>{formatDate(payroll_deadline)}</b>
                  </Text>
                ) : (
                  <Text variant={TypographyStyles.BODY_SM}>
                    Ready to compensate your employees for their hard work? Run
                    a payroll cycle by clicking the button below.
                  </Text>
                )}

                <div className="pt-3 w-full md:w-1/2">
                  <Button
                    onClick={() =>
                      navigate(`/payroll/run_payroll/process/${payroll_uuid}`)
                    }
                    size="md"
                    className="w-full"
                    iconLeft={faCircleArrowRight}
                  >
                    Run Payroll
                  </Button>
                </div>

                {isAutoPayroll && (
                  <Text variant={TypographyStyles.BODY_SM}>
                    Please click the above button if you would like to run
                    payroll manually or run it before the automated date.
                  </Text>
                )}
              </>
            )}
          </div>

          {/* Right Column */}
          <div className="w-full md:w-1/2 bg-white p-4">
            <PayrollCalendar payroll={upcomingPayroll} />
          </div>
        </div>
      </Card>
    </div>
  )
}
